import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Share2, Copy, MessageCircle, Mail, Link2, X, Check } from "lucide-react";

import { useAuth } from "@/context/AuthContext";

/**
 * Share panel for comics — opens a nice popover with:
 *  - Native Web Share API (mobile "share sheet") when available
 *  - Copy link (ADMIN-ONLY — vendors don't get this button)
 *  - WhatsApp direct link
 *  - Email mailto
 * Works everywhere; on desktop we skip the native share button.
 */
export default function ShareComic({ title, url, message, testid = "comic-share" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { user } = useAuth() || {};
  const isAdmin = user?.role === "admin" || user?.role === "owner";

  const shareText = `${message} — ${url}`;
  const hasNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  const doNative = async () => {
    try {
      await navigator.share({ title, text: message, url });
      setOpen(false);
    } catch {
      /* user cancelled */
    }
  };

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Enlace copiado");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const mailUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(shareText)}`;

  return (
    <div className="relative inline-block" data-testid={testid}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testid}-open`}
        className="brutal-btn bg-canales-blue text-white px-4 py-2 flex items-center gap-2 font-display font-black"
      >
        <Share2 className="w-5 h-5" /> Compartir
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.96 }}
            className="absolute right-0 mt-2 w-80 z-50"
            data-testid={`${testid}-panel`}
          >
            <div className="brutal bg-white overflow-hidden">
              <div className="flex items-start justify-between p-4 bg-canales-blue text-white">
                <div>
                  <div className="font-display font-black text-lg leading-tight">
                    Muebleria Canales · Cómic
                  </div>
                  <div className="text-xs opacity-90 mt-1">{title}</div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="close"
                  className="text-white/80 hover:text-white"
                  data-testid={`${testid}-close`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-3 space-y-2">
                {hasNativeShare && (
                  <button
                    onClick={doNative}
                    data-testid={`${testid}-native`}
                    className="w-full brutal-sm p-3 flex items-center gap-3 bg-canales-yellow font-display font-bold"
                  >
                    <Share2 className="w-5 h-5" />
                    Compartir con el sistema
                  </button>
                )}
                <button
                  onClick={doCopy}
                  data-testid={`${testid}-copy`}
                  disabled={!isAdmin}
                  title={isAdmin ? undefined : "Solo el admin puede copiar el link"}
                  className={`w-full brutal-sm p-3 flex items-center gap-3 font-display font-bold ${
                    isAdmin ? "bg-white" : "bg-neutral-100 opacity-50 cursor-not-allowed"
                  }`}
                >
                  {copied ? (
                    <>
                      <Check className="w-5 h-5 text-green-700" />
                      ¡Enlace copiado!
                    </>
                  ) : (
                    <>
                      <Copy className="w-5 h-5" />
                      {isAdmin ? "Copiar enlace" : "Copiar enlace (solo admin)"}
                    </>
                  )}
                </button>
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`${testid}-whatsapp`}
                  className="w-full brutal-sm p-3 flex items-center gap-3 bg-white font-display font-bold hover:bg-green-100"
                >
                  <MessageCircle className="w-5 h-5 text-green-600" />
                  Enviar por WhatsApp
                </a>
                <a
                  href={mailUrl}
                  data-testid={`${testid}-email`}
                  className="w-full brutal-sm p-3 flex items-center gap-3 bg-white font-display font-bold hover:bg-blue-50"
                >
                  <Mail className="w-5 h-5 text-blue-700" />
                  Enviar por email
                </a>

                <div className="mt-2 brutal-sm p-2 bg-canales-cream flex items-center gap-2 text-[11px] font-body">
                  <Link2 className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{url}</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
